import { BRAND_SEARCH_ALIASES } from "./search-aliases";
import { compactSearch, normalizeSearch, transliterateCyrillic } from "./search-normalization";
import type { ProductView } from "./types";

interface BrandEntry { key: string; forms: Set<string> }
interface BrandIndex { exact: Map<string, Set<string>>; byLength: Map<number, Array<{ form: string; key: string }>> }
export interface QualifiedBrandQuery { brandKey: string; productQuery: string }
const indexes = new WeakMap<readonly ProductView[], BrandIndex>();
const queryCaches = new WeakMap<readonly ProductView[], Map<string, QualifiedBrandQuery | null>>();
const reviewedBrands = new Map(Object.entries(BRAND_SEARCH_ALIASES).flatMap(([key, aliases]) =>
  [key, ...aliases].map(form => [compactSearch(form), key] as const)));
function canonicalBrand(value: unknown): string {
  return reviewedBrands.get(compactSearch(value)) ?? normalizeSearch(value);
}

/** Only public catalog brand fields and reviewed spelling aliases. No network,
 * user data, language guessing or similarity between unrelated product names. */
function brandIndex(products: readonly ProductView[]): BrandIndex {
  const cached = indexes.get(products);
  if (cached) return cached;
  const entries = new Map<string, BrandEntry>();
  const add = (value: string) => {
    const key = canonicalBrand(value);
    if (key.length < 2 || /^(?:none|null|unknown|без бренда|no brand)$/u.test(key)) return;
    const compact = compactSearch(key);
    const entry = entries.get(compact) ?? { key, forms: new Set<string>() };
    for (const form of [key, ...(BRAND_SEARCH_ALIASES[key] ?? [])]) {
      entry.forms.add(compactSearch(form));
      entry.forms.add(compactSearch(transliterateCyrillic(form)));
    }
    entries.set(compact, entry);
  };
  // Known spellings remain a constraint even in a week with no offers from
  // that brand. This adds vocabulary, never products or a claim of availability.
  Object.keys(BRAND_SEARCH_ALIASES).forEach(add);
  products.forEach(product => {
    if (!product.brand) return;
    add(product.brand);
    product.brand.split(/\s*[\/;]\s*/u).forEach(add);
  });
  const result: BrandIndex = { exact: new Map(), byLength: new Map() };
  for (const entry of entries.values()) for (const form of entry.forms) {
    const keys = result.exact.get(form) ?? new Set<string>();
    keys.add(entry.key); result.exact.set(form, keys);
    const rows = result.byLength.get(form.length) ?? [];
    rows.push({ form, key: entry.key }); result.byLength.set(form.length, rows);
  }
  indexes.set(products, result);
  return result;
}

function oneEdit(left: string, right: string): boolean {
  if (Math.abs(left.length - right.length) > 1) return false;
  if (left.length === right.length) {
    const changed = [...left].flatMap((char, index) => char === right[index] ? [] : [index]);
    return changed.length === 1 || (changed.length === 2 && changed[1] === changed[0]! + 1 &&
      left[changed[0]!] === right[changed[1]!] && left[changed[1]!] === right[changed[0]!]);
  }
  const [short, long] = left.length < right.length ? [left, right] : [right, left];
  let at = 0;
  while (at < short.length && short[at] === long[at]) at += 1;
  return short.slice(at) === long.slice(at + 1);
}

function findBrand(products: readonly ProductView[], text: string, fuzzy: boolean): string | undefined {
  const forms = new Set([compactSearch(text), compactSearch(transliterateCyrillic(text))]);
  const index = brandIndex(products);
  const exact = new Set([...forms].flatMap(form => [...index.exact.get(form) ?? []]));
  if (exact.size === 1) return exact.values().next().value;
  if (exact.size || !fuzzy) return undefined;
  // A typo correction requires one unique long brand, never a short common word.
  const near = new Set<string>();
  for (const form of forms) {
    if (form.length < 5) continue;
    for (let length = Math.max(5, form.length - 1); length <= form.length + 1; length += 1) {
      for (const candidate of index.byLength.get(length) ?? []) {
        if (oneEdit(form, candidate.form)) near.add(candidate.key);
        if (near.size > 1) return undefined;
      }
    }
  }
  return near.size === 1 ? near.values().next().value : undefined;
}

export function isCatalogBrand(products: readonly ProductView[], text: string): boolean {
  return Boolean(findBrand(products, text, false));
}

export function productMatchesSearchBrand(product: ProductView, brandKey: string): boolean {
  const key = canonicalBrand(product.brand);
  const components = product.brand?.split(/\s*[\/;]\s*/u).map(canonicalBrand) ?? [];
  if (compactSearch(key) === compactSearch(brandKey) || key.startsWith(`${brandKey} `) ||
    components.some(component => compactSearch(component) === compactSearch(brandKey))) return true;
  if (key) return false; // A different explicit brand cannot match a note/title mention.
  const title = normalizeSearch([product.product_ru, product.product_ro, product.product_uk].filter(Boolean).join(" "));
  return ` ${title} `.includes(` ${brandKey} `);
}

/** A branded product is still one AND query. Brand-only and multi-brand requests
 * keep the established lexical search instead of silently dropping a term. */
export function qualifiedBrandQuery(products: readonly ProductView[], input: string): QualifiedBrandQuery | undefined {
  const text = normalizeSearch(input).replace(/(?<!\d)\.|\.(?!\d)/gu, " ").replace(/\s+/gu, " ").trim();
  const cache = queryCaches.get(products) ?? new Map<string, QualifiedBrandQuery | null>();
  queryCaches.set(products, cache);
  if (cache.has(text)) return cache.get(text) ?? undefined;
  const words = text.split(" ").filter(Boolean);
  let result: QualifiedBrandQuery | undefined;
  if (words.length > 1 && words.length <= 24 && !isCatalogBrand(products, text)) {
    for (const fuzzy of [false, true]) {
      const matches: Array<QualifiedBrandQuery & { length: number }> = [];
      for (let length = Math.min(6, words.length - 1); length >= 1; length -= 1) {
        for (let start = 0; start + length <= words.length; start += 1) {
          const brandKey = findBrand(products, words.slice(start, start + length).join(" "), fuzzy);
          if (!brandKey) continue;
          const rest = [...words.slice(0, start), ...words.slice(start + length)].join(" ");
          // Two explicitly named brands are not a product+brand interpretation.
          if (isCatalogBrand(products, rest)) continue;
          matches.push({ brandKey, productQuery: rest, length });
        }
      }
      matches.sort((a, b) => b.length - a.length);
      const top = matches[0];
      if (top && !matches.some(match => match.length === top.length &&
        (match.brandKey !== top.brandKey || match.productQuery !== top.productQuery))) {
        result = { brandKey: top.brandKey, productQuery: top.productQuery };
        break;
      }
    }
  }
  if (cache.size >= 96) cache.delete(cache.keys().next().value!);
  cache.set(text, result ?? null);
  return result;
}

export function joinsProductBrand(products: readonly ProductView[], left: string, right: string): boolean {
  // Only a bare adjacent brand may refine an item. "milk, cheese President"
  // must remain two items, as must "President, Nivea".
  if (Boolean(findBrand(products, left, true)) === Boolean(findBrand(products, right, true))) return false;
  return Boolean(qualifiedBrandQuery(products, `${left} ${right}`));
}
